import { inject, Injectable, DOCUMENT } from '@angular/core';

import { WINDOW } from '@ng-web-apis/common';
import { PlatformService } from './platform.service';

export interface GoogleTagManagerConfig {
  id: string | null;
  gtm_auth?: string;
  gtm_preview?: string;

  [key: string]: string | null | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private _platform = inject(PlatformService);
  private _window = inject<Window>(WINDOW);
  private _document = inject<Document>(DOCUMENT);


  private _isLoaded = false;
  public config: GoogleTagManagerConfig = { id: 'GTM-WDP3DQG' };

  public getDataLayer(): any[] {
    const window = this._window;
    window['dataLayer'] = window['dataLayer'] || [];
    return window['dataLayer'];
  }

  public async addGtmToDom() {
    if (this._isLoaded || !this._platform.isBrowser) return;

    const doc: any = this._document;

    try {
      this._pushOnDataLayer({
        'gtm.start': new Date().getTime(),
        'event': 'gtm.js',
      });

      const gtmScript = doc.createElement('script');
      gtmScript.id = 'GTMscript';
      gtmScript.async = true;
      gtmScript.src = this._applyGtmQueryParams(
        this.config.gtm_resource_path || 'https://www.googletagmanager.com/gtm.js',
      );

      gtmScript.addEventListener('load', () => {
        this._isLoaded = true;
      });

      gtmScript.addEventListener('error', (e) => {
        console.warn('Failed to load:', e);
      });

      doc.head.insertBefore(gtmScript, doc.head.firstChild);

      const isPrivate = await this._isSafariPrivateMode();
      if (!isPrivate) {
        const iframe = doc.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.config.id}`;
        iframe.setAttribute('height', '0');
        iframe.setAttribute('width', '0');
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        doc.body.appendChild(iframe);
      }
    } catch (err) {
      console.warn('Error initializing:', err);
    }
  }

  private _isSafariPrivateMode(): Promise<boolean> {
    return new Promise((resolve) => {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (!isSafari) return resolve(false);

      const db = indexedDB.open('test');
      db.onerror = () => resolve(true);
      db.onsuccess = () => resolve(false);
    });
  }

  private _pushOnDataLayer(obj: object): void {
    const dataLayer = this.getDataLayer();
    dataLayer.push(obj);
  }

  private _applyGtmQueryParams(url: string): string {
    if (url.indexOf('?') === -1) {
      url += '?';
    }

    return (
      url +
      Object.keys(this.config)
        .filter(k => this.config[k])
        .map(k => `${k}=${this.config[k]}`)
        .join('&')
    );
  }


  /**
   * Game click event
   * @param gameid
   */
  public gameClickEvent(gameid: string) {
    this._pushOnDataLayer({
      event: 'game_click',
      gameid,
    });
  }

  /**
   * Banner Click Event
   * @param gameid
   */
  public bannerClickEvent(bannerOrder: string) {
    this._pushOnDataLayer({
      event: 'click_on_main_banner',
      eventCategory: 'main_banner',
      eventLabel: 'event_label',
      eventValue: bannerOrder,
    });
  }

  /**
   * Popups event
   * @param modal
   */
  public popupEvent(popup: string) {
    this._pushOnDataLayer({
      event: '21bitpopup',
      event_action: `open-${popup}`,
      event_category: popup,
    });
  }
}
